import { t } from './kit';
import {
  REEL_PALETTES,
  REEL_STYLES,
  reelCopyIn,
  type ReelCopyTx,
  type ReelPaletteId,
  type ReelStyleId,
} from './reel';
import type { VideoGalleryFormat, VideoTemplateDef } from './types';

/** Gallery entries built from the motion reel blueprints (see reel.ts). */

function reel(def: {
  id: string;
  name: ReturnType<typeof t>;
  description: ReturnType<typeof t>;
  format: VideoGalleryFormat;
  style: ReelStyleId;
  palette: ReelPaletteId;
  copy: ReelCopyTx;
}): VideoTemplateDef {
  const style = REEL_STYLES[def.style];
  // Scene structure depends only on how many words there are, the same in every language
  const shape = reelCopyIn(def.copy, { tx: (v) => (typeof v === 'string' ? v : v.en) });
  return {
    id: def.id,
    category: 'motion',
    name: def.name,
    description: def.description,
    format: def.format,
    scenes: style.scenes(shape),
    sound: true,
    build: (b) => style.build(b, reelCopyIn(def.copy, b), REEL_PALETTES[def.palette]),
  };
}

const LAUNCH_CODE = [
  'export const Launch = () => (',
  '  <Scene fps={60} size="9:16">',
  '    <Fill color="#FF4A0D" />',
  '    <Title text="NEW" />',
  '    <Product src="nova.png" spin />',
  '  </Scene>',
  ');',
];

export const TEMPLATES_REEL: VideoTemplateDef[] = [
  reel({
    id: 'prompt-launch',
    style: 'launch',
    palette: 'ember',
    format: 'wide',
    name: t(
      'Prompt launch',
      'Запуск із промпту',
      'Запуск из промпта',
      'Premiera z promptu',
      'Lansare din prompt',
      'Lanzamiento por prompt',
    ),
    description: t(
      'Viewfinder HUD, a terminal prompt, kinetic words, code typing in 3D',
      'HUD видошукача, промпт у терміналі, кінетичні слова, код у 3D',
      'HUD видоискателя, промпт в терминале, кинетические слова, код в 3D',
      'HUD wizjera, prompt w terminalu, kinetyczne słowa, kod w 3D',
      'HUD de vizor, prompt în terminal, cuvinte cinetice, cod în 3D',
      'HUD de visor, prompt en terminal, palabras cinéticas, código en 3D',
    ),
    copy: {
      brand: 'Nova',
      hud: t(
        'LAUNCH FILM · 2026',
        'ФІЛЬМ ЗАПУСКУ · 2026',
        'ФИЛЬМ ЗАПУСКА · 2026',
        'FILM PREMIEROWY · 2026',
        'FILM DE LANSARE · 2026',
        'FILM DE LANZAMIENTO · 2026',
      ),
      prompt: t(
        'make me a launch video',
        'зроби відео запуску',
        'сделай видео запуска',
        'zrób film premierowy',
        'fă-mi un video de lansare',
        'hazme un vídeo de lanzamiento',
      ),
      words: [
        t('Prompt.', 'Промпт.', 'Промпт.', 'Prompt.', 'Prompt.', 'Prompt.'),
        t('Motion.', 'Рух.', 'Движение.', 'Ruch.', 'Mișcare.', 'Movimiento.'),
        t('Launch.', 'Запуск.', 'Запуск.', 'Start.', 'Lansare.', 'Lanzamiento.'),
      ],
      headline: t(
        'Code in. Film out.',
        'Код на вході. Фільм на виході.',
        'Код на входе. Фильм на выходе.',
        'Kod na wejściu. Film na wyjściu.',
        'Cod la intrare. Film la ieșire.',
        'Entra código. Sale película.',
      ),
      tagline: t(
        'Type a prompt. Motion made easy.',
        'Напишіть промпт — і все рухається.',
        'Напишите промпт — и всё движется.',
        'Wpisz prompt — i wszystko rusza.',
        'Scrie un prompt — și totul prinde viață.',
        'Escribe un prompt y todo se mueve.',
      ),
      cta: t('Ship it.', 'Запускай.', 'Запускай.', 'Wypuść to.', 'Lansează.', 'Lánzalo.'),
      topics: [
        t('Launches', 'Запуски', 'Запуски', 'Premiery', 'Lansări', 'Lanzamientos'),
        t('Explainers', 'Пояснення', 'Объяснения', 'Objaśnienia', 'Explicații', 'Explicativos'),
        t(
          'Brand films',
          'Бренд-фільми',
          'Бренд-фильмы',
          'Filmy marki',
          'Filme de brand',
          'Películas de marca',
        ),
      ],
      stat: {
        value: 48,
        prefix: '+',
        suffix: '%',
        label: t(
          'faster edits',
          'швидший монтаж',
          'быстрее монтаж',
          'szybszy montaż',
          'montaj mai rapid',
          'edición más rápida',
        ),
      },
      code: LAUNCH_CODE,
    },
  }),
  reel({
    id: 'motion-showreel',
    style: 'showreel',
    palette: 'signal',
    format: 'wide',
    name: t(
      'Motion showreel',
      'Моушн-шоурил',
      'Моушн-шоурил',
      'Showreel motion',
      'Showreel motion',
      'Showreel de motion',
    ),
    description: t(
      'Particles, a 3D dot floor, a ribbon, rings of text and a signature',
      'Частинки, 3D-підлога з точок, стрічка, кільця тексту й підпис',
      'Частицы, 3D-пол из точек, лента, кольца текста и подпись',
      'Cząsteczki, podłoga 3D z kropek, wstęga, pierścienie tekstu i podpis',
      'Particule, podea 3D din puncte, panglică, inele de text și semnătură',
      'Partículas, suelo 3D de puntos, cinta, anillos de texto y firma',
    ),
    copy: {
      brand: 'Orbit Studio',
      hud: t(
        'MOTION REEL 2026',
        'МОУШН-РІЛ 2026',
        'МОУШН-РИЛ 2026',
        'MOTION REEL 2026',
        'MOTION REEL 2026',
        'MOTION REEL 2026',
      ),
      prompt: 'render --reel',
      words: ['Rhythm.', 'Timing.', 'Easing.'],
      headline: t(
        'Every frame on purpose',
        'Кожен кадр — з наміром',
        'Каждый кадр — с умыслом',
        'Każda klatka z zamysłem',
        'Fiecare cadru cu un scop',
        'Cada fotograma con intención',
      ),
      tagline: t(
        'Motion design · art direction',
        'Моушн-дизайн · арт-дирекшн',
        'Моушн-дизайн · арт-дирекшн',
        'Motion design · art direction',
        'Motion design · direcție artistică',
        'Motion design · dirección de arte',
      ),
      cta: t(
        "hello@orbit.studio · let's talk",
        'hello@orbit.studio · пишіть',
        'hello@orbit.studio · пишите',
        'hello@orbit.studio · napisz',
        'hello@orbit.studio · scrie-ne',
        'hello@orbit.studio · escríbenos',
      ),
      topics: [
        t(
          'Motion design',
          'Моушн-дизайн',
          'Моушн-дизайн',
          'Motion design',
          'Motion design',
          'Motion design',
        ),
        t('Typography', 'Типографіка', 'Типографика', 'Typografia', 'Tipografie', 'Tipografía'),
        t(
          '3D and particles',
          '3D і частинки',
          '3D и частицы',
          '3D i cząsteczki',
          '3D și particule',
          '3D y partículas',
        ),
        t(
          'Art direction',
          'Арт-дирекшн',
          'Арт-дирекшн',
          'Art direction',
          'Direcție artistică',
          'Dirección de arte',
        ),
      ],
      stat: { value: 120, suffix: ' BPM', label: 'tempo' },
      code: ['const reel = motion()'],
    },
  }),
  reel({
    id: 'kinetic-manifesto',
    style: 'manifesto',
    palette: 'acid',
    format: 'vertical',
    name: t(
      'Kinetic manifesto',
      'Кінетичний маніфест',
      'Кинетический манифест',
      'Kinetyczny manifest',
      'Manifest cinetic',
      'Manifiesto cinético',
    ),
    description: t(
      'Words slam on the beat, extruded 3D type, a word wall and a sticker',
      'Слова б’ють у ритм, об’ємний 3D-текст, стіна слів і стікер',
      'Слова бьют в ритм, объёмный 3D-текст, стена слов и стикер',
      'Słowa uderzają w rytm, tekst 3D, ściana słów i naklejka',
      'Cuvinte pe ritm, text 3D, zid de cuvinte și autocolant',
      'Palabras al ritmo, texto 3D, muro de palabras y pegatina',
    ),
    copy: {
      brand: 'Volt',
      hud: 'VOLT · 2026',
      prompt: 'volt --loud',
      words: [
        t('Bold.', 'Сміливо.', 'Смело.', 'Odważnie.', 'Curajos.', 'Audaz.'),
        t('Loud.', 'Гучно.', 'Громко.', 'Głośno.', 'Tare.', 'Fuerte.'),
        t('Yours.', 'Твоє.', 'Твоё.', 'Twoje.', 'Al tău.', 'Tuyo.'),
      ],
      headline: t(
        'Make it move',
        'Хай рухається',
        'Пусть движется',
        'Niech się rusza',
        'Pune-l în mișcare',
        'Hazlo moverse',
      ),
      tagline: t(
        'Energy drink for night owls',
        'Енергетик для сов',
        'Энергетик для сов',
        'Energetyk dla nocnych marków',
        'Energizant pentru bufnițe',
        'Bebida energética para noctámbulos',
      ),
      cta: t('Grab one', 'Візьми', 'Возьми', 'Weź jeden', 'Ia unul', 'Pilla uno'),
      topics: [
        t('No sugar', 'Без цукру', 'Без сахара', 'Bez cukru', 'Fără zahăr', 'Sin azúcar'),
        t('No crash', 'Без спаду', 'Без спада', 'Bez zjazdu', 'Fără cădere', 'Sin bajón'),
        t('All night', 'Всю ніч', 'Всю ночь', 'Całą noc', 'Toată noaptea', 'Toda la noche'),
      ],
      stat: { value: 0, suffix: '', label: '' },
      code: ['volt()'],
    },
  }),
  reel({
    id: 'data-story',
    style: 'data',
    palette: 'cobalt',
    format: 'square',
    name: t(
      'Data story',
      'Історія в цифрах',
      'История в цифрах',
      'Historia w liczbach',
      'Povestea în cifre',
      'Historia en cifras',
    ),
    description: t(
      'A counting number, progress ring, charts, a split-flap board and a ticker',
      'Лічильник, кільце прогресу, графіки, табло й біржова стрічка',
      'Счётчик, кольцо прогресса, графики, табло и биржевая лента',
      'Licznik, pierścień postępu, wykresy, tablica i pasek notowań',
      'Contor, inel de progres, grafice, panou și bandă de cotații',
      'Contador, anillo de progreso, gráficos, panel y cinta de cotizaciones',
    ),
    copy: {
      brand: 'Metric',
      hud: t('Q3 RESULTS', 'ПІДСУМКИ Q3', 'ИТОГИ Q3', 'WYNIKI Q3', 'REZULTATE Q3', 'RESULTADOS Q3'),
      prompt: 'report --q3',
      words: [
        t('Now live', 'Вже працює', 'Уже работает', 'Już działa', 'Acum live', 'Ya en vivo'),
        t('40 cities', '40 міст', '40 городов', '40 miast', '40 de orașe', '40 ciudades'),
      ],
      headline: t(
        'Our best quarter yet',
        'Наш найкращий квартал',
        'Наш лучший квартал',
        'Nasz najlepszy kwartał',
        'Cel mai bun trimestru',
        'Nuestro mejor trimestre',
      ),
      tagline: t(
        'Thank you for building with us',
        'Дякуємо, що будуєте з нами',
        'Спасибо, что строите с нами',
        'Dziękujemy, że budujecie z nami',
        'Mulțumim că construiți cu noi',
        'Gracias por construir con nosotros',
      ),
      cta: t(
        'Read the report',
        'Читати звіт',
        'Читать отчёт',
        'Przeczytaj raport',
        'Citește raportul',
        'Leer el informe',
      ),
      topics: [
        t(
          'Weekly growth',
          'Тижневе зростання',
          'Рост за неделю',
          'Wzrost tygodniowy',
          'Creștere săptămânală',
          'Crecimiento semanal',
        ),
        t('Revenue', 'Виручка', 'Выручка', 'Przychód', 'Venituri', 'Ingresos'),
        'NPS +71',
        'CHURN -3%',
      ],
      stat: {
        value: 72,
        suffix: '%',
        label: t('conversion', 'конверсія', 'конверсия', 'konwersja', 'conversie', 'conversión'),
      },
      code: ['report()'],
    },
  }),
  reel({
    id: 'brand-film',
    style: 'brand',
    palette: 'sunset',
    format: 'vertical',
    name: t(
      'Brand film',
      'Бренд-фільм',
      'Бренд-фильм',
      'Film marki',
      'Film de brand',
      'Película de marca',
    ),
    description: t(
      'A tracked-out title over a photo, light leaks and blurred words',
      'Розріджений титр на фото, засвітки й розмиті слова',
      'Разреженный титр на фото, засветки и размытые слова',
      'Rozstrzelony tytuł na zdjęciu, prześwietlenia i rozmyte słowa',
      'Titlu rarefiat pe foto, scurgeri de lumină și cuvinte estompate',
      'Título espaciado sobre foto, fugas de luz y palabras difuminadas',
    ),
    copy: {
      brand: 'North',
      hud: t(
        'BRAND FILM · 01',
        'БРЕНД-ФІЛЬМ · 01',
        'БРЕНД-ФИЛЬМ · 01',
        'FILM MARKI · 01',
        'FILM DE BRAND · 01',
        'PELÍCULA · 01',
      ),
      prompt: 'north',
      words: [
        t('Wild.', 'Дико.', 'Дико.', 'Dziko.', 'Sălbatic.', 'Salvaje.'),
        t('Quiet.', 'Тихо.', 'Тихо.', 'Cicho.', 'Liniștit.', 'Tranquilo.'),
        t('Free.', 'Вільно.', 'Свободно.', 'Wolno.', 'Liber.', 'Libre.'),
      ],
      headline: t(
        'Go where the map ends',
        'Іди туди, де закінчується мапа',
        'Иди туда, где кончается карта',
        'Idź tam, gdzie kończy się mapa',
        'Mergi unde se termină harta',
        'Ve donde acaba el mapa',
      ),
      tagline: t(
        'Outdoor gear since 1998',
        'Спорядження з 1998 року',
        'Снаряжение с 1998 года',
        'Sprzęt outdoorowy od 1998',
        'Echipament outdoor din 1998',
        'Equipo outdoor desde 1998',
      ),
      cta: t(
        'Explore the collection',
        'Дивитися колекцію',
        'Смотреть коллекцию',
        'Zobacz kolekcję',
        'Vezi colecția',
        'Ver la colección',
      ),
      topics: ['North'],
      stat: { value: 1998, label: '' },
      code: ['north()'],
      photo: 'mountain-lake',
    },
  }),
  reel({
    id: 'dev-tool-launch',
    style: 'launch',
    palette: 'mint',
    format: 'vertical',
    name: t(
      'Dev tool launch',
      'Запуск для розробників',
      'Запуск для разработчиков',
      'Premiera narzędzia dev',
      'Lansare unealtă dev',
      'Lanzamiento para devs',
    ),
    description: t(
      'Terminal deploy, code in 3D, kinetic words on mint',
      'Деплой у терміналі, код у 3D, кінетичні слова на м’ятному',
      'Деплой в терминале, код в 3D, кинетические слова на мятном',
      'Wdrożenie w terminalu, kod w 3D, kinetyczne słowa na miętowym',
      'Deploy în terminal, cod în 3D, cuvinte cinetice pe mentă',
      'Despliegue en terminal, código en 3D, palabras cinéticas en menta',
    ),
    copy: {
      brand: 'Pulse API',
      hud: 'PULSE · v2.0',
      prompt: 'npx pulse deploy --prod',
      words: [
        t('Build.', 'Збирай.', 'Собирай.', 'Buduj.', 'Construiește.', 'Construye.'),
        t('Test.', 'Тестуй.', 'Тестируй.', 'Testuj.', 'Testează.', 'Prueba.'),
        t('Deploy.', 'Деплой.', 'Деплой.', 'Wdrażaj.', 'Publică.', 'Despliega.'),
      ],
      headline: t(
        'Ship APIs in minutes',
        'API за хвилини',
        'API за минуты',
        'API w kilka minut',
        'API în câteva minute',
        'APIs en minutos',
      ),
      tagline: t(
        'Edge functions with zero config',
        'Edge-функції без налаштувань',
        'Edge-функции без настройки',
        'Funkcje edge bez konfiguracji',
        'Funcții edge fără configurare',
        'Funciones edge sin configuración',
      ),
      cta: t(
        'Start free',
        'Почати безкоштовно',
        'Начать бесплатно',
        'Zacznij za darmo',
        'Începe gratuit',
        'Empieza gratis',
      ),
      topics: ['Edge', 'Logs', 'Deploys'],
      stat: { value: 99, suffix: '%', label: 'uptime' },
      code: [
        "import { pulse } from 'pulse'",
        '',
        "export default pulse.get('/hello', () => {",
        "  return { hello: 'world' }",
        '})',
      ],
    },
  }),
];
